import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  CheckCircleOutlined,
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { Badge, Button, Dialog, Skeleton, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { HanMucHoacCauGoc } from '../../../../components/shared';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { useChiTietTomTat, usePhanCongTuTomTat, useSuaTomTat } from '../../hooks/useTomTat';
import { daTomTat, dangXuLy, tyLeRutGon } from '../../nghiep-vu/tom-tat';
import { TomTatMarkdown } from './TomTatMarkdown';

function Khung({
  tieuDe,
  icon,
  phu,
  nhan,
  nhanAi,
  children,
}: {
  tieuDe: string;
  icon: ReactNode;
  phu?: string;
  nhan?: string;
  nhanAi?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col overflow-hidden rounded-lg border', nhanAi ? 'border-violet-200' : 'border-default')}>
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b px-3.5 py-2.5 text-xs font-semibold',
          nhanAi ? 'border-violet-100 bg-violet-50 text-violet-700' : 'border-default bg-subtle text-content-primary',
        )}
      >
        <span className="flex items-center gap-1.5">
          {icon} {tieuDe}
          {nhan && <Badge variant="warning" className="ml-1.5">{nhan}</Badge>}
        </span>
        {phu && <span className="text-2xs font-normal text-content-muted">{phu}</span>}
      </div>
      <div className="max-h-[56vh] min-h-[340px] overflow-y-auto px-4 py-3.5 text-sm">{children}</div>
    </div>
  );
}

/**
 * Hộp "So sánh văn bản gốc & bản tóm tắt" của bản cũ: hai cột, sửa bản tóm tắt tại chỗ, xuất `.doc`,
 * AI phân công nhiệm vụ. Văn bản đang chạy nền thì hộp tự cập nhật khi xong (query chi tiết hỏi lại 4 giây).
 */
export function SoSanhTomTatDialog({
  id,
  suaNgay,
  coTheChay,
  coTheSua,
  coThePhanCong,
  dangChay,
  onClose,
  onChay,
  onXuat,
}: {
  id: string | null;
  /** Mở thẳng ở chế độ sửa (mục "Sửa bản tóm tắt" của bảng). */
  suaNgay: boolean;
  coTheChay: boolean;
  coTheSua: boolean;
  coThePhanCong: boolean;
  dangChay: boolean;
  onClose: () => void;
  onChay: (id: string) => void;
  onXuat: (id: string) => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const chiTiet = useChiTietTomTat(id);
  const sua = useSuaTomTat();
  const phanCong = usePhanCongTuTomTat();
  const [dangSua, setDangSua] = useState(suaNgay);
  const [banSua, setBanSua] = useState<string | null>(null);
  const [daPhanCong, setDaPhanCong] = useState<number | null>(null);

  if (!id) return null;
  const d = chiTiet.data;
  const xong = d ? daTomTat(d.status) : false;
  const tyLe = d ? tyLeRutGon(d.reduction_ratio) : null;
  const vanSua = banSua ?? d?.summary_content ?? '';

  const batDauSua = () => {
    setBanSua(d?.summary_content ?? '');
    setDangSua(true);
  };
  const huySua = () => {
    setBanSua(null);
    setDangSua(false);
  };
  const luu = () => {
    const v = vanSua.trim();
    if (!v) {
      toast.error(t('vanBanTomTat.tomTatTrong'));
      return;
    }
    sua.mutate({ id, summary: v }, { onSuccess: () => huySua() });
  };

  const footer = !d ? (
    <Button variant="outline" onClick={onClose}>
      {t('vanBanChung.dong')}
    </Button>
  ) : !xong ? (
    <Button variant="outline" onClick={onClose}>
      {t('vanBanChung.dong')}
    </Button>
  ) : dangSua && xong ? (
    <div className="flex flex-wrap justify-end gap-2">
      <Button variant="outline" onClick={huySua}>
        {t('chung.huy')}
      </Button>
      <Button variant="primary" icon={<CheckOutlined />} loading={sua.isPending} onClick={luu}>
        {sua.isPending ? t('chung.dangLuu') : t('vanBanTomTat.luuTomTat')}
      </Button>
    </div>
  ) : (
    <div className="flex flex-wrap justify-end gap-2">
      <Button variant="outline" onClick={onClose}>
        {t('vanBanChung.dong')}
      </Button>
      {coThePhanCong && (
        <Button
          variant="outline"
          className="ai-glow"
          loading={phanCong.isPending}
          icon={<Sparkles size={14} className="ai-sparkle" />}
          onClick={() => phanCong.mutate(id, { onSuccess: (r) => setDaPhanCong(r.created_count) })}
        >
          {t('vanBanTomTat.aiPhanCong')}
        </Button>
      )}
      {coTheSua && (
        <Button variant="outline" icon={<EditOutlined />} onClick={batDauSua}>
          {t('vanBanTomTat.suaTomTat')}
        </Button>
      )}
      <Button variant="primary" icon={<DownloadOutlined />} onClick={() => onXuat(id)}>
        {t('vanBanTomTat.xuatTomTat')}
      </Button>
    </div>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      width={1100}
      destroyOnHidden
      title={
        <span className="flex items-center gap-2">
          <FileTextOutlined className="text-primary-500" />
          {t('vanBanTomTat.soSanhTieuDe')}
        </span>
      }
      footer={footer}
    >
      {chiTiet.isLoading ? (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-80 w-full" />
        </div>
      ) : chiTiet.error || !d ? (
        <div role="alert" className="flex items-center justify-between gap-3 py-3">
          <span className="text-sm text-danger-700">{t('vanBanTomTat.loiChiTiet', { loi: dichLoi(chiTiet.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void chiTiet.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <>
          {daPhanCong !== null && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-4 py-2.5 text-sm text-success-700">
              <CheckCircleOutlined className="shrink-0" />
              <span>
                <b>{t('vanBanTomTat.phanCongXong')}</b> {t('vanBanTomTat.phanCongXongMo', { so: soVN(daPhanCong, locale) })}
              </span>
            </div>
          )}
          <div className="mb-3">
            <div className="text-sm font-semibold text-content-primary">{d.document_name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-content-muted">
              <Badge variant="neutral">{d.document_type}</Badge>
              <span>• {ngayVN(d.created_at)}</span>
              {d.creator_name && <span>• {d.creator_name}</span>}
              {tyLe !== null && xong && <span>• {t('vanBanTomTat.rutGonSoVoiGoc', { so: tyLe })}</span>}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
            <Khung
              tieuDe={t('vanBanTomTat.vanBanGoc')}
              icon={<FileTextOutlined className="text-primary-500" />}
              phu={t('vanBanTomTat.soKyTu', { so: soVN(d.original_char_count ?? 0, locale) })}
            >
              <div className="whitespace-pre-wrap leading-relaxed text-content-secondary">{d.original_content}</div>
            </Khung>
            <Khung
              tieuDe={t('vanBanTomTat.banTomTatAi')}
              icon={<ThunderboltOutlined className="text-violet-500" />}
              nhanAi
              phu={xong ? t('vanBanTomTat.soKyTu', { so: soVN(d.summary_char_count ?? 0, locale) }) : undefined}
              nhan={dangSua && xong ? t('vanBanTomTat.dangChinhSua') : undefined}
            >
              {!xong ? (
                <div className="flex h-full flex-col items-center justify-center py-10 text-center text-content-muted">
                  <ThunderboltOutlined className="mb-2.5 text-3xl" />
                  <div className="mb-1">
                    {dangXuLy(d.status) || dangChay ? t('vanBanTomTat.dangTomTat') : t('vanBanTomTat.chuaDuocTomTat')}
                  </div>
                  {d.status === 'FAILED' && d.error_message && (
                    <HanMucHoacCauGoc
                      cau={d.error_message}
                      bienThe="gon"
                      className="mb-3 mt-2 w-full max-w-md text-left"
                      nhanThuLai={coTheChay ? t('vanBanTomTat.aiTomTatNgay') : undefined}
                    >
                      <p className="mb-3 max-w-sm text-xs text-danger-600">{d.error_message}</p>
                    </HanMucHoacCauGoc>
                  )}
                  {coTheChay && !dangXuLy(d.status) && (
                    <Button
                      variant="primary"
                      className="ai-glow mt-2.5"
                      loading={dangChay}
                      icon={<Sparkles size={14} className="ai-sparkle" />}
                      onClick={() => onChay(id)}
                    >
                      {t('vanBanTomTat.aiTomTatNgay')}
                    </Button>
                  )}
                </div>
              ) : dangSua ? (
                <Textarea
                  variant="borderless"
                  aria-label={t('vanBanTomTat.banTomTatAi')}
                  value={vanSua}
                  onChange={(e) => setBanSua(e.target.value)}
                  className="h-full min-h-[320px] resize-none px-0 leading-relaxed"
                />
              ) : (
                <TomTatMarkdown van={d.summary_content ?? ''} />
              )}
            </Khung>
          </div>
        </>
      )}
    </Dialog>
  );
}
